import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SitePlanMap from "@/components/SitePlanMap";
import { ArrowRight, ShieldCheck, KeyRound, FileSignature, MapPin, Truck, Fence, CheckCircle2, Video } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const AERIAL = "https://images.pexels.com/photos/8783587/pexels-photo-8783587.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const GRAVEL = "https://images.pexels.com/photos/16466054/pexels-photo-16466054.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const [lots, setLots] = useState([]);
  useEffect(() => {
    api.get("/lots").then((r) => setLots(r.data)).catch(() => {});
  }, []);

  const available = lots.filter((l) => l.status === "available").length;
  const minRent = lots.length ? Math.min(...lots.map((l) => l.monthly_rent)) : 2903;

  return (
    <div data-testid="landing-page" className="min-h-screen text-[#2C2F36]">
      <Navbar transparent />

      {/* HERO — image-free, type-driven layout */}
      <section className="relative pt-16 pb-20 lg:pt-28 lg:pb-28 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="overline text-[#0B0C0E]" data-testid="hero-overline">
            <MapPin className="inline w-3 h-3 mr-1" /> 18961 118A AVE NW · EDMONTON · ALBERTA
          </div>
          <h1 className="mt-6 font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.92] text-[#0B0C0E]">
            Industrial<br/>
            Storage Lots,<br/>
            <span className="relative inline-block">
              <span className="relative z-10">Leased Monthly.</span>
              <span className="absolute left-0 right-0 bottom-1 h-4 lg:h-7 bg-[#FACC15] -z-0" aria-hidden />
            </span>
          </h1>
          <p className="mt-8 text-[#2C2F36] text-lg max-w-2xl leading-relaxed">
            14 fully-developed acres. 16 gravel-pad lots ranging from <strong className="text-[#0B0C0E]">0.50 to 1.00 acres</strong>.
            Smaller sizes available <strong className="text-[#0B0C0E]">starting from 5,000 sq ft</strong> — and any custom size on request.
            Fenced, lit, 24/7 access. Book online — sign your lease in minutes.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 items-center">
            <a
              href="#site-plan"
              className="inline-flex items-center gap-3 bg-[#0B0C0E] hover:bg-black text-white font-bold px-8 py-4 uppercase tracking-wide transition-colors border-l-4 border-[#FACC15]"
              data-testid="hero-cta-book"
            >
              View Site Plan <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-3 border-2 border-[#0B0C0E] hover:bg-[#FACC15] text-[#0B0C0E] font-bold px-8 py-4 uppercase tracking-wide text-sm transition-colors"
              data-testid="hero-cta-learn"
            >
              How it works
            </a>
          </div>

          {/* Stats strip */}
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#0B0C0E] border-2 border-[#0B0C0E] max-w-3xl">
            <Stat label="Lots Available" value={available} testId="stat-available" />
            <Stat label="From / Month" value={fmtMoney(minRent)} testId="stat-min-rent" />
            <Stat label="Total Acres" value="14.0" testId="stat-acres" />
            <Stat label="Lot Count" value="16" testId="stat-count" />
          </div>
        </div>
      </section>

      {/* SITE PLAN */}
      <section id="site-plan" className="py-24 md:py-28 border-t border-[#E5E3DA] bg-[#FCFBF6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-7">
              <div className="overline">01 — INTERACTIVE PLAN</div>
              <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl uppercase tracking-tight text-[#0B0C0E]">
                Click your lot.<br/>
                <span className="bg-[#FACC15] px-3">Lock it in 5 minutes.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <p className="text-[#56606E] text-base leading-relaxed">
                Each lot below is gravel-finished, ready for trucks, trailers, containers and equipment.
                Pricing is locked: ${minRent.toLocaleString()}–$5,827/month, depending on size.
              </p>
            </div>
          </div>

          <SitePlanMap lots={lots} />
        </div>
      </section>

      {/* CUSTOM SIZES CALLOUT */}
      <section data-testid="custom-sizes-section" className="bg-[#FACC15] border-y-2 border-[#0B0C0E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#0B0C0E] font-semibold">Custom Sizes</div>
            <h3 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-[#0B0C0E] leading-[0.95]">
              Need a smaller lot?<br/>
              <span className="bg-[#0B0C0E] text-[#FACC15] px-3 inline-block mt-2">From 5,000 sq ft up.</span>
            </h3>
            <p className="mt-4 text-[#0B0C0E] max-w-2xl">
              Don&apos;t see a size that fits? We sub-divide to suit. Tell us your requirements — equipment count, hookups, gates,
              container drops — and we&apos;ll quote a custom pad for you.
            </p>
          </div>
          <a
            href="/contact"
            className="bg-[#0B0C0E] hover:bg-black text-white font-bold px-8 py-4 uppercase tracking-wide text-sm inline-flex items-center gap-3 border-l-4 border-white whitespace-nowrap flex-shrink-0"
            data-testid="custom-size-cta"
          >
            Request a Custom Quote <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Reference pricing tiers */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-14">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#0B0C0E] font-semibold mb-3">Reference Pricing · plus GST</div>
          <div className="grid sm:grid-cols-3 gap-px bg-[#0B0C0E] border-2 border-[#0B0C0E]" data-testid="custom-pricing-tiers">
            <PriceTier sqft={5000} price={950} />
            <PriceTier sqft={10000} price={1850} />
            <PriceTier sqft={15000} price={2590} />
          </div>
          <p className="mt-3 text-xs text-[#0B0C0E]/80">Indicative rates for smaller / custom lots. Final pricing depends on hookups, term, and location on the plan.</p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-24 border-t border-[#E5E3DA] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline">02 — PROCESS</div>
          <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl uppercase tracking-tight max-w-3xl text-[#0B0C0E]">
            Three steps. Zero paperwork chasing.
          </h2>

          <div className="mt-14 grid md:grid-cols-3 gap-px bg-[#0B0C0E] border-2 border-[#0B0C0E]">
            <StepCard n="01" icon={<KeyRound className="w-6 h-6" />} title="Pick a Lot" desc="Browse the interactive site plan. Compare size and rent. Click an available lot to start." />
            <StepCard n="02" icon={<ShieldCheck className="w-6 h-6" />} title="Book & Pay" desc="Enter your business details. Pay first month's rent + $500 refundable deposit via Stripe." />
            <StepCard n="03" icon={<FileSignature className="w-6 h-6" />} title="Sign Lease" desc="Your lease is auto-generated. Sign with one click. Download your PDF. Move in immediately." />
          </div>
        </div>
      </section>

      {/* AMENITIES */}
      <section id="amenities" className="py-24 md:py-32 bg-[#FAFAF6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline">03 — THE PROPERTY</div>
            <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl uppercase tracking-tight text-[#0B0C0E]">
              Built for heavy use.<br/>
              Maintained like an asset.
            </h2>
            <p className="mt-6 text-[#56606E] leading-relaxed">
              Located in Edmonton&apos;s NW industrial corridor with direct access to major routes.
              Engineered for commercial fleets, oilfield equipment, RV storage, and contractor staging.
            </p>
            <ul className="mt-8 space-y-3">
              <Feature icon={<KeyRound className="w-4 h-4 text-[#0B0C0E]" />} text="Gated entry & exit — every tenant gets a personalised access code" />
              <Feature icon={<Video className="w-4 h-4 text-[#0B0C0E]" />} text="Security cameras — monitored 24/7" />
              <Feature icon={<Fence className="w-4 h-4 text-[#0B0C0E]" />} text="Perimeter fenced — security-grade chain-link" />
              <Feature icon={<Truck className="w-4 h-4 text-[#0B0C0E]" />} text="20-metre central road — fits semi rigs and oversize loads" />
              <Feature icon={<ShieldCheck className="w-4 h-4 text-[#0B0C0E]" />} text="24/7 access · well-lit · all-weather" />
              <Feature icon={<CheckCircle2 className="w-4 h-4 text-[#0B0C0E]" />} text="Gravel pads — drainage-graded for heavy loads" />
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={AERIAL} alt="Aerial fleet parking" className="w-full h-72 object-cover border-2 border-[#0B0C0E]" data-testid="amenity-img-1" />
            <img src={GRAVEL} alt="Gravel pad parking" className="w-full h-72 object-cover mt-10 border-2 border-[#0B0C0E]" data-testid="amenity-img-2" />
          </div>
        </div>
      </section>

      {/* LOCATION (Google Map) */}
      <section id="location" className="py-24 border-t border-[#E5E3DA] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-stretch">
          <div className="lg:col-span-5 flex flex-col">
            <div className="overline">04 — LOCATION</div>
            <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl uppercase tracking-tight text-[#0B0C0E] leading-[0.95]">
              Find us in<br/>
              <span className="bg-[#FACC15] px-3 inline-block mt-1">NW Edmonton.</span>
            </h2>
            <address className="mt-8 not-italic">
              <div className="font-display font-black text-2xl text-[#0B0C0E] uppercase tracking-tight">14 Acres</div>
              <div className="mt-2 text-[#2C2F36]">18961 118A Avenue NW<br/>Edmonton, Alberta, Canada</div>
            </address>
            <p className="mt-6 text-[#56606E] text-sm leading-relaxed">
              Easy access from major routes — minutes from Yellowhead Trail &amp; Anthony Henday Drive. 24/7 gated entry with your personal access code.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=18961+118A+Avenue+NW,+Edmonton,+AB"
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#0B0C0E] hover:bg-black text-white font-bold px-6 py-3 uppercase tracking-wide text-sm border-l-4 border-[#FACC15]"
                data-testid="get-directions-cta"
              >
                Get Directions <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://www.google.com/maps/search/?api=1&query=18961+118A+Avenue+NW,+Edmonton,+AB"
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 border-2 border-[#0B0C0E] hover:bg-[#FACC15] text-[#0B0C0E] font-bold px-6 py-3 uppercase tracking-wide text-sm"
                data-testid="open-map-cta"
              >
                Open in Maps
              </a>
            </div>
          </div>

          <div className="lg:col-span-7" data-testid="location-map">
            <div className="border-2 border-[#0B0C0E] bg-[#FCFBF6] aspect-[4/3] lg:aspect-auto lg:h-full min-h-[360px] overflow-hidden">
              <iframe
                title="14 Acres location map"
                src="https://www.google.com/maps?q=18961+118A+Avenue+NW,+Edmonton,+AB&output=embed"
                width="100%" height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 border-t border-[#E5E3DA] bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="overline">04 — FAQ</div>
          <h2 className="mt-3 font-display font-black text-4xl sm:text-5xl uppercase tracking-tight text-[#0B0C0E]">
            Common questions
          </h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            <FaqItem q="What is the minimum lease term?" a="Standard term is 12 months. Custom terms can be discussed by contacting lease@14acre.ca." v="1" />
            <FaqItem q="Do you offer smaller or custom lot sizes?" a="Yes. Lots smaller than what's shown on the plan are available starting from 5,000 sq ft, and we can sub-divide to any custom size you need. Email lease@14acre.ca with your requirements (equipment, hookups, drop-off needs) and we'll quote you within one business day." v="7" />
            <FaqItem q="How does site access work?" a="Every tenant receives a personalised entry/exit code on lease activation. Gates open 24/7 with your code, and the entire site is monitored by security cameras around the clock." v="6" />
            <FaqItem q="What is charged at booking?" a="First month's rent plus a $500 refundable security deposit. Subsequent rent is invoiced on the 1st of each month." v="2" />
            <FaqItem q="Is the deposit refundable?" a="Yes — $500 is fully refundable within 30 days of lease termination, subject to lot inspection." v="3" />
            <FaqItem q="What can I store on my lot?" a="Vehicles, trailers, containers, equipment, and similar commercial assets. Hazardous materials and residential use are not permitted." v="4" />
            <FaqItem q="How do I sign the lease?" a="After payment, you'll be redirected to e-sign with a typed signature. Your PDF lease is downloadable instantly." v="5" />
          </Accordion>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId} className="bg-white p-4">
    <div className="text-[10px] tracking-[0.22em] uppercase text-[#56606E] font-semibold">{label}</div>
    <div className="mt-1 font-display font-black text-2xl text-[#0B0C0E]">{value}</div>
  </div>
);

const StepCard = ({ n, icon, title, desc }) => (
  <div className="bg-white p-10 hover:bg-[#FCFBF6] transition-colors group relative">
    <div className="flex items-start justify-between mb-8">
      <div className="w-12 h-12 border-2 border-[#0B0C0E] text-[#0B0C0E] flex items-center justify-center group-hover:bg-[#FACC15] transition-colors">
        {icon}
      </div>
      <span className="font-display font-black text-5xl text-[#E5E3DA]">{n}</span>
    </div>
    <h3 className="font-display font-black text-2xl uppercase tracking-tight text-[#0B0C0E]">{title}</h3>
    <p className="mt-3 text-[#56606E] leading-relaxed text-sm">{desc}</p>
  </div>
);

const Feature = ({ icon, text }) => (
  <li className="flex items-center gap-3 border-b border-[#E5E3DA] pb-3">
    <span className="w-6 h-6 inline-flex items-center justify-center bg-[#FACC15]">{icon}</span>
    <span className="text-base text-[#0B0C0E]">{text}</span>
  </li>
);

const FaqItem = ({ q, a, v }) => (
  <AccordionItem value={v} className="border-[#E5E3DA]" data-testid={`faq-${v}`}>
    <AccordionTrigger className="text-left font-medium text-lg text-[#0B0C0E] hover:no-underline hover:text-[#56606E]">{q}</AccordionTrigger>
    <AccordionContent className="text-[#56606E] text-base leading-relaxed">{a}</AccordionContent>
  </AccordionItem>
);

const PriceTier = ({ sqft, price }) => (
  <a href={`/contact?size=${sqft}`} className="bg-white hover:bg-[#FCFBF6] p-5 block transition" data-testid={`tier-${sqft}`}>
    <div className="text-[10px] tracking-[0.22em] uppercase text-[#56606E] font-semibold">Smaller Lot</div>
    <div className="mt-2 font-display font-black text-3xl text-[#0B0C0E]">{sqft.toLocaleString()}<span className="text-base font-medium text-[#56606E]"> sq ft</span></div>
    <div className="mt-2 font-display font-black text-xl text-[#0B0C0E]">${price.toLocaleString()}<span className="text-xs font-medium text-[#56606E]">/mo + GST</span></div>
  </a>
);
